import { resolveSessionPermissions } from "../../../utils/permissionUtils";
import {
  ISF_CONSOLIDATED_PERMISSION_LEGACY,
  expandStoredIsfPermissionKeys,
} from "../../../constants/isfPermissionConsolidation";

export {
  ISF_CONSOLIDATED_PERMISSION_LEGACY,
  expandStoredIsfPermissionKeys,
};

const hasAny = (permissions, keys = []) =>
  keys.some((key) => permissions.includes(key));

export function hasIsfPermission(userData, key, { legacyKeys = [] } = {}) {
  const { isSystemAdmin, permissions } = resolveSessionPermissions(userData);
  if (isSystemAdmin || permissions.includes("AdminRole")) return true;
  if (permissions.includes(key)) return true;
  return hasAny(permissions, legacyKeys);
}

function hasConsolidatedIsfPermission(userData, canonicalKey) {
  const legacyKeys = ISF_CONSOLIDATED_PERMISSION_LEGACY[canonicalKey] || [];
  return hasIsfPermission(userData, canonicalKey, { legacyKeys });
}

export function canViewIsfDashboard(userData) {
  return hasIsfPermission(userData, "ISF_DASHBOARD", { legacyKeys: ["VIEW_ISF"] });
}

export function canViewIsfReports(userData) {
  return hasIsfPermission(userData, "ISF_REPORTS", { legacyKeys: ["VIEW_ISF"] });
}

export function canViewFolderLibrary(userData) {
  return hasConsolidatedIsfPermission(userData, "FOLDER_LIBRARY");
}

export function canAddFolderLibrary(userData) {
  return hasConsolidatedIsfPermission(userData, "FOLDER_LIBRARY");
}

export function canEditFolderLibrary(userData) {
  return hasConsolidatedIsfPermission(userData, "FOLDER_LIBRARY");
}

export function canDeleteFolderLibrary(userData) {
  return hasConsolidatedIsfPermission(userData, "FOLDER_LIBRARY");
}

export function canManageFolderLibrary(userData) {
  return canViewFolderLibrary(userData);
}

export function canViewMilestoneLibrary(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_LIBRARY");
}

export function canAddMilestone(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_LIBRARY");
}

export function canEditMilestone(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_LIBRARY");
}

export function canDeleteMilestone(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_LIBRARY");
}

export function canManageMilestoneLibrary(userData) {
  return canViewMilestoneLibrary(userData);
}

export function canViewMilestonePlan(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_PLAN");
}

export function canAddMilestonePlan(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_PLAN");
}

export function canEditMilestonePlan(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_PLAN");
}

export function canDeleteMilestonePlan(userData) {
  return hasConsolidatedIsfPermission(userData, "MILESTONE_PLAN");
}

export function canManageMilestonePlan(userData) {
  return canViewMilestonePlan(userData);
}

export function canViewTemplate(userData) {
  return hasConsolidatedIsfPermission(userData, "TEMPLATE_MANAGER");
}

export function canAddTemplate(userData) {
  return hasConsolidatedIsfPermission(userData, "TEMPLATE_MANAGER");
}

export function canEditTemplate(userData) {
  return hasConsolidatedIsfPermission(userData, "TEMPLATE_MANAGER");
}

export function canDeleteTemplate(userData) {
  return hasConsolidatedIsfPermission(userData, "TEMPLATE_MANAGER");
}

export function canManageTemplate(userData) {
  return canViewTemplate(userData);
}

export function canEditTemplatePlaceholders(userData) {
  return canEditTemplate(userData);
}

export function canSelectTemplateMilestonePlan(userData) {
  return canManageTemplate(userData);
}

/** Apply placeholders / waive — milestone or template editors. */
export function canManageStudyPlaceholders(userData) {
  return (
    canManageMilestoneLibrary(userData) ||
    canManageTemplate(userData) ||
    hasIsfPermission(userData, "MANAGE_STUDY_PLACEHOLDERS")
  );
}

export function canAccessAuditLog(userData) {
  return hasConsolidatedIsfPermission(userData, "AUDIT_LOG");
}

export function canDownloadAuditLog(userData) {
  return canAccessAuditLog(userData);
}

export function canRequestAuditLog(userData) {
  return canAccessAuditLog(userData);
}

/** Permissions removed from the role card — governed by binder rules instead. */
export const ISF_PERMISSIONLESS_ACTIONS = new Set([
  "newFile",
  "replaceFile",
  "deleteFile",
  "moveNode",
  "lockFile",
  "unlockFile",
  "signFile",
  "supersededFile",
]);
